import React, { useCallback, useEffect, useRef, useState, useMemo, Fragment } from 'react';
import { createPortal, unstable_batchedUpdates } from 'react-dom';
import {
  CancelDrop,
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  Modifiers,
  useDroppable,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  KeyboardCoordinateGetter,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  AnimateLayoutChanges,
  SortableContext,
  useSortable,
  arrayMove,
  defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
  SortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { coordinateGetter as multipleContainersCoordinateGetter } from './multipleContainersKeyboardCoordinates';

import { Item, Container, ContainerProps } from './components';
import { R } from '@core/utils/r';
import { Collapse } from 'reactstrap';

export default {
  title: 'Presets/Sortable/Multiple Containers',
};

const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({ ...args, wasDragging: true });

function DroppableContainer({
  children,
  columns = 1,
  disabled,
  id,
  items,
  style,
  renderColumn,
  collapsable = true,
  ...props
}: ContainerProps & {
  disabled?: boolean;
  id: UniqueIdentifier;
  items: UniqueIdentifier[];
  style?: React.CSSProperties;
  collapsable?: boolean;
}) {
  const { active, attributes, isDragging, listeners, over, setNodeRef, transition, transform } = useSortable({
    id,
    data: {
      type: 'container',
      children: items,
    },
    animateLayoutChanges,
    disabled: disabled,
  });
  const isOverContainer = over ? (id === over.id && active?.data.current?.type !== 'container') || items.includes(over.id) : false;

  return (
    <Container
      renderColumn={renderColumn}
      ref={disabled ? undefined : setNodeRef}
      style={{
        ...style,
        transition,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
        borderBottom: collapsable ? '1px solid rgba(0, 0, 0, 0.08)' : undefined,
      }}
      hover={isOverContainer}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      columns={columns}
      {...props}
    >
      {children}
    </Container>
  );
}

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

type Items = Record<UniqueIdentifier, UniqueIdentifier[]>;

interface SortableSectionStepsProps<S extends any, I extends any> {
  adjustScale?: boolean;
  cancelDrop?: CancelDrop;
  columns?: number;
  containerStyle?: React.CSSProperties;
  coordinateGetter?: KeyboardCoordinateGetter;
  getItemStyles?(args: {
    value: UniqueIdentifier;
    index: number;
    overIndex: number;
    isDragging: boolean;
    containerId: UniqueIdentifier;
    isSorting: boolean;
    isDragOverlay: boolean;
  }): React.CSSProperties;
  wrapperStyle?(args: { index: number }): React.CSSProperties;
  itemCount?: number;
  handle?: boolean;
  renderItem: (item: I, placeholder: boolean, index: number, sectionIndex: number, section?: S) => React.ReactElement;
  strategy?: SortingStrategy;
  modifiers?: Modifiers;
  minimal?: boolean;
  trashable?: boolean;
  scrollable?: boolean;
  vertical?: boolean;
  sections: S[];
  sectionItemsKey: keyof S;
  sectionKey: keyof S;
  itemKey: keyof I;
  closeKey?: keyof S;
  hideItemsOnSectionMove?: boolean;
  disabled?: boolean;
  renderSection: (section: S) => React.ReactElement;
  noItemsRender?: (section: S) => React.ReactElement;
  renderSectionFooter?: (section: S) => React.ReactElement;
  onSort?: (sections: UniqueIdentifier[], items: Items) => void;
  collapsable?: boolean;
}

export const TRASH_ID = 'void';

export function SortableSectionSteps<S extends any, I extends any>({
  adjustScale = false,
  itemCount = 3,
  cancelDrop,
  columns,
  handle = false,
  containerStyle,
  coordinateGetter = multipleContainersCoordinateGetter,
  getItemStyles = () => ({}),
  wrapperStyle = () => ({}),
  minimal = false,
  modifiers,
  renderItem,
  strategy = verticalListSortingStrategy,
  vertical = false,
  scrollable,
  sectionKey,
  sections,
  sectionItemsKey,
  itemKey,
  closeKey,
  renderSection,
  renderSectionFooter,
  noItemsRender,
  hideItemsOnSectionMove = false,
  onSort,
  disabled,
  collapsable = true,
}: SortableSectionStepsProps<S, I>) {
  const [items, setItems] = useState<Items>(() => ({}));
  const itemsRef = useRef<Items>({});

  const [containers, setContainers] = useState(Object.keys(items) as UniqueIdentifier[]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  // const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer = activeId ? containers.includes(activeId) : false;

  const activeStep = useMemo(() => {
    const allSteps = R.flatMap(sections, s => s[sectionItemsKey] || []);
    return allSteps.find(s => s[itemKey] == activeId);
  }, [activeId, itemKey, sectionItemsKey, sections]);

  const allSteps = useMemo(() => R.flatMap(sections, s => s[sectionItemsKey] || []), [sectionItemsKey, sections]);
  const allSections = useMemo(() => sections, [sections]);

  useEffect(() => {
    const newItems: Items = {};
    sections.forEach((section, sectionIndex) => {
      const sectionId = section[sectionKey];
      const steps = (section[sectionItemsKey] || []) as any[];
      newItems[`${sectionId}`] = steps.map(step => R.toString(step[itemKey]) as UniqueIdentifier);
    });
    setItems(newItems);
    setContainers(sections.map(s => s[sectionKey]) as UniqueIdentifier[]);
  }, [itemCount, itemKey, sectionItemsKey, sectionKey, sections]);

  // Keep itemsRef updated with current items state
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    args => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(container => container.id in items),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (overId === TRASH_ID) {
          // If the intersecting droppable is the trash, return early
          // Remove this if you're not using trashable functionality in your app
          return intersections;
        }

        if (overId in items) {
          const containerItems = items[overId];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(container => container.id !== overId && containerItems.includes(container.id)),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items],
  );

  const [clonedItems, setClonedItems] = useState<Items | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    // useSensor(KeyboardSensor, {
    //   coordinateGetter,
    // }),
  );
  const findContainer = (id: UniqueIdentifier) => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find(key => items[key].includes(id));
  };

  const getIndex = (id: UniqueIdentifier) => {
    const container = findContainer(id);

    if (!container) {
      return -1;
    }

    const index = items[container].indexOf(id);

    return index;
  };

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }
    setIsDragging(false);
    setActiveId(null);
    setClonedItems(null);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  if (R.keys(items).length == 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      onDragStart={({ active }) => {
        setIsDragging(true);
        setActiveId(active.id);
        setClonedItems(items);
      }}
      onDragOver={({ active, over }) => {
        const overId = over?.id;
        if (overId == null || overId === TRASH_ID || active.id in items) {
          return;
        }

        const overContainer = findContainer(overId);
        const activeContainer = findContainer(active.id);

        if (!overContainer || !activeContainer) {
          return;
        }

        // setOverId(overContainer);
        if (activeContainer !== overContainer) {
          setItems(items => {
            const activeItems = items[activeContainer];
            const overItems = items[overContainer];
            const overIndex = overItems.indexOf(overId);
            const activeIndex = activeItems.indexOf(active.id);

            let newIndex: number;

            if (overId in items) {
              newIndex = overItems.length + 1;
            } else {
              const isBelowOverItem = over && active.rect.current.translated && active.rect.current.translated.top > over.rect.top + over.rect.height;

              const modifier = isBelowOverItem ? 1 : 0;

              newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            recentlyMovedToNewContainer.current = true;

            return {
              ...items,
              [activeContainer]: items[activeContainer].filter(item => item !== active.id),
              [overContainer]: [
                ...items[overContainer].slice(0, newIndex),
                items[activeContainer][activeIndex],
                ...items[overContainer].slice(newIndex, items[overContainer].length),
              ],
            };
          });
        }
      }}
      onDragEnd={({ active, over }) => {
        let shouldCallOnSort = false;
        let finalContainers = containers;
        let finalItems = items;

        setIsDragging(false);

        if (active.id in items && over?.id) {
          const activeIndex = containers.indexOf(active.id);
          const overIndex = containers.indexOf(over.id);
          finalContainers = arrayMove(containers, activeIndex, overIndex);

          setContainers(finalContainers);
          shouldCallOnSort = true;
        }

        const activeContainer = findContainer(active.id);

        if (!activeContainer) {
          setActiveId(null);
          return;
        }

        const overId = over?.id;

        if (overId == null) {
          setActiveId(null);
          return;
        }
        const overContainer = findContainer(overId);

        if (overContainer) {
          const activeIndex = items[activeContainer].indexOf(active.id);
          const overIndex = items[overContainer].indexOf(overId);

          if (activeIndex !== overIndex || activeContainer !== overContainer) {
            if (activeContainer !== overContainer) {
              // Cross-container move - use the ref to get the current items state
              finalItems = itemsRef.current;
              shouldCallOnSort = true;
            } else {
              // Same container move - update the items
              finalItems = {
                ...items,
                [overContainer]: arrayMove(items[overContainer], activeIndex, overIndex),
              };
              setItems(finalItems);
              shouldCallOnSort = true;
            }
          }
        }

        setActiveId(null);

        // Call onSort only once at the end of drag operation if there was a change
        // if (shouldCallOnSort && onSort) {
        //   // Call onSort with the final updated values
        //   setTimeout(() => {
        onSort(finalContainers, finalItems);
        //   }, 0);
        // }
      }}
      cancelDrop={cancelDrop}
      onDragCancel={onDragCancel}
      modifiers={modifiers}
    >
      <div
        style={{
          display: 'inline-grid',
          boxSizing: 'border-box',
          width: '100%',
          gridAutoFlow: vertical ? 'row' : 'column',
        }}
      >
        <SortableContext items={[...containers]} strategy={vertical ? verticalListSortingStrategy : horizontalListSortingStrategy}>
          {containers.map((containerId, sectionIndex) => {
            const section = allSections.find(s => s[sectionKey] == containerId)!;
            return (
              <DroppableContainer
                key={containerId}
                id={containerId}
                label={minimal ? undefined : `Column ${containerId}`}
                columns={columns}
                items={items[containerId]}
                scrollable={scrollable}
                style={containerStyle}
                unstyled={minimal}
                renderColumn={() => renderSection(section)}
                disabled={disabled}
                collapsable={collapsable}
              >
                {!closeKey && (
                  <SortableContext items={items[containerId]} strategy={strategy}>
                    {items[containerId].map((value, index) => {
                      const step = allSteps.find(s => s[itemKey] == value);
                      return (
                        <SortableItem
                          disabled={isSortingContainer}
                          key={value}
                          id={value}
                          index={index}
                          handle={handle}
                          style={getItemStyles}
                          wrapperStyle={wrapperStyle}
                          renderItem={step ? () => renderItem(step, activeId ? true : false, index, sectionIndex, section) : undefined}
                          containerId={containerId}
                          getIndex={getIndex}
                        />
                      );
                    })}
                    {items[containerId].length === 0 && noItemsRender && noItemsRender(section)}
                  </SortableContext>
                )}
                {collapsable === true && (activeId ? !containers.includes(activeId) : true) && (
                  <Fragment>
                    <Collapse
                      isOpen={isDragging ? true : closeKey ? (R.get(section, closeKey) !== undefined ? !R.get(section, closeKey) : false) : false}
                    >
                      <SortableContext items={items[containerId]} strategy={strategy}>
                        {items[containerId].map((value, index) => {
                          const step = allSteps.find(s => s[itemKey] == value);
                          return (
                            <SortableItem
                              disabled={isSortingContainer}
                              key={value}
                              id={value}
                              index={index}
                              handle={handle}
                              style={getItemStyles}
                              wrapperStyle={wrapperStyle}
                              renderItem={step ? () => renderItem(step, activeId ? true : false, index, sectionIndex, section) : undefined}
                              containerId={containerId}
                              getIndex={getIndex}
                            />
                          );
                        })}
                      </SortableContext>
                      {renderSectionFooter && renderSectionFooter(section)}
                    </Collapse>
                  </Fragment>
                )}
              </DroppableContainer>
            );
          })}
        </SortableContext>
      </div>
      {createPortal(
        <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation} zIndex={1450}>
          {activeId
            ? containers.includes(activeId)
              ? renderContainerDragOverlay(activeId, hideItemsOnSectionMove)
              : renderSortableItemDragOverlay(activeId, activeStep)
            : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );

  function renderSortableItemDragOverlay(id: UniqueIdentifier, step?: any) {
    return (
      <Item
        value={id}
        handle={handle}
        style={getItemStyles({
          containerId: findContainer(id) as UniqueIdentifier,
          overIndex: -1,
          index: getIndex(id),
          value: id,
          isSorting: true,
          isDragging: true,
          isDragOverlay: true,
        })}
        wrapperStyle={wrapperStyle({ index: 0 })}
        renderItem={step ? () => renderItem(step, true, 0, -1) : undefined} // -1 for sectionIndex in overlay
        dragOverlay
      />
    );
  }

  function renderContainerDragOverlay(containerId: UniqueIdentifier, hideItemsOnSectionMove = false) {
    return (
      <Container
        label={`Column ${containerId}`}
        columns={columns}
        style={
          {
            //   height: '100%',
          }
        }
        shadow
        renderColumn={() => renderSection(allSections.find(s => s[sectionKey] == containerId)!)}
        unstyled={false}
      >
        {!hideItemsOnSectionMove &&
          items[containerId].map((item, index) => (
            <Item
              key={item}
              value={item}
              handle={handle}
              style={getItemStyles({
                containerId,
                overIndex: -1,
                index: getIndex(item),
                value: item,
                isDragging: false,
                isSorting: false,
                isDragOverlay: false,
              })}
              wrapperStyle={wrapperStyle({ index })}
              renderItem={() => renderItem(allSteps.find(s => s[itemKey] == item)!, true, index, -1)} // -1 for sectionIndex in overlay
            />
          ))}
      </Container>
    );
  }

  function getNextContainerId() {
    const containerIds = Object.keys(items);
    const lastContainerId = containerIds[containerIds.length - 1];

    return String.fromCharCode(lastContainerId.charCodeAt(0) + 1);
  }
}

interface SortableItemProps {
  containerId: UniqueIdentifier;
  id: UniqueIdentifier;
  index: number;
  handle: boolean;
  disabled?: boolean;
  style(args: any): React.CSSProperties;
  getIndex(id: UniqueIdentifier): number;
  renderItem?(): React.ReactElement;
  wrapperStyle({ index }: { index: number }): React.CSSProperties;
}

function SortableItem({ disabled, id, index, handle, renderItem, style, containerId, getIndex, wrapperStyle }: SortableItemProps) {
  const { setNodeRef, setActivatorNodeRef, listeners, isDragging, isSorting, over, overIndex, transform, transition } = useSortable({
    id,
  });
  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      value={id}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={handle ? { ref: setActivatorNodeRef } : undefined}
      index={index}
      wrapperStyle={wrapperStyle({ index })}
      style={style({
        index,
        value: id,
        isDragging,
        isSorting,
        overIndex: over ? getIndex(over.id) : overIndex,
        containerId,
      })}
      transition={transition}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={listeners}
      renderItem={renderItem}
    />
  );
}

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}
